import React from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { Employee } from 'types/interfaces';

import { TaskService } from 'components/services/TaskService';

import { withDialogFooterSpacing } from '../shared/dialogFooterSpacing';

interface TaskCardAutoEmployeeAssignmentDialogProps {
  visible: boolean;
  onHide: () => void;
  response: any;
  taskId?: number | string;
  showSaveDepartmentNotice?: boolean;
  onApplyAssignment: (employees: Employee[]) => void;
}

const errorMessages: Record<string, string> = {
  employeeNotFound: 'Не найдено ни одного работника',
  notEnoughEmployees: 'Найдено меньше работников, чем требуется',
  departmentWithoutEmployees: 'В участке нет работников',
  taskTypeError: 'Не указан тип наряда',
  taskZeroEmployeeCount: 'В типе наряда не указано количество работников',
  taskStartError: 'Не указано плановое начало',
  taskFinishError: 'Не указано плановое завершение',
  taskNotFound: 'Наряд с таким идентификатором не найден',
  planningAlgorithmError: 'В типе наряда не задан алгоритм распределения'
};

const isAssignmentSuccessful = (response: any): boolean => {
  if (!response?.isSuccess) return false;
  const result = response?.value?.[0];
  if (!result) return false;
  if (Array.isArray(result.errors) && result.errors.length > 0) return false;
  return Array.isArray(result.employees) && result.employees.length > 0;
};

const getAssignmentErrors = (response: any): string[] => {
  if (typeof response === 'string') return [response];
  const errors = response?.value?.[0]?.errors;
  return Array.isArray(errors) ? errors : [];
};

const getSuggestedEmployees = (response: any): Employee[] => {
  const employees = response?.value?.[0]?.employees;
  if (!Array.isArray(employees)) return [];
  return employees.map((e: any) => ({
    id: e.id,
    fullName: e.fullName ?? e.label ?? '',
    label: e.fullName ?? e.label ?? '',
    key: e.id !== undefined && e.id !== null ? String(e.id) : ''
  }));
};

export const TaskCardAutoEmployeeAssignmentDialog: React.FC<
  TaskCardAutoEmployeeAssignmentDialogProps
> = ({
  visible,
  onHide,
  response,
  taskId,
  showSaveDepartmentNotice = false,
  onApplyAssignment
}) => {
  if (showSaveDepartmentNotice) {
    return (
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Невозможно подобрать исполнителей"
        style={{ minWidth: '500px', width: '500px' }}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex gap-3 justify-content-end">
            <Button
              label="Закрыть"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={onHide}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 p-2">
          <div className="flex align-items-center gap-2">
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '1.5rem', color: '#f59e0b' }}
            />
            <span className="font-medium">Действие недоступно</span>
          </div>

          <div
            className="flex flex-column p-3 border-1 border-round"
            style={{
              backgroundColor: '#fffbeb',
              borderColor: '#fde68a',
              borderRadius: '8px',
              gap: '8px'
            }}
          >
            <div className="flex align-items-center gap-2">
              <i
                className="pi pi-info-circle"
                style={{ fontSize: '1rem', color: '#d97706' }}
              />
              <span style={{ color: '#92400e', fontWeight: 500 }}>
                Сначала необходимо сохранить выбранный участок
              </span>
            </div>
          </div>
        </div>
      </Dialog>
    );
  }

  if (!response) return null;

  const isSuccess = isAssignmentSuccessful(response);

  if (isSuccess) {
    const employees = getSuggestedEmployees(response);

    const handleConfirm = async () => {
      if (taskId === undefined || taskId === null) {
        onHide();
        return;
      }
      try {
        await TaskService.AutoTaskEmployeeAssignment([Number(taskId)], false);
        onApplyAssignment(employees);
      } catch (error) {
        console.error('Error applying employee auto-assignment:', error);
      } finally {
        onHide();
      }
    };

    return (
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Исполнители подобраны"
        style={{ minWidth: '500px', width: '500px' }}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex gap-3 justify-content-end">
            <Button
              label="Отмена"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={onHide}
            />
            <Button
              label="Подтвердить назначение"
              icon="pi pi-check"
              onClick={handleConfirm}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 p-2">
          <div className="flex align-items-center gap-2">
            <i
              className="pi pi-check-circle"
              style={{ fontSize: '1.5rem', color: '#22c55e' }}
            />
            <span className="font-medium">Подобраны следующие исполнители:</span>
          </div>

          <div
            className="flex flex-column p-3 border-1 border-round"
            style={{
              backgroundColor: '#f0fdf4',
              borderColor: '#bbf7d0',
              borderRadius: '8px',
              gap: '6px'
            }}
          >
            {employees.map((e) => (
              <div
                key={String(e.id)}
                className="flex align-items-center gap-2"
              >
                <i
                  className="pi pi-user"
                  style={{ fontSize: '1rem', color: '#16a34a' }}
                />
                <span
                  className="font-medium"
                  style={{ color: '#166534' }}
                >
                  {e.fullName}
                </span>
              </div>
            ))}
          </div>

          <div className="text-sm text-gray-500 mt-1">
            После подтверждения исполнители будут назначены на наряд.
          </div>
        </div>
      </Dialog>
    );
  }

  const errors = getAssignmentErrors(response);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Ошибка подбора"
      style={{ minWidth: '500px', width: '500px' }}
      pt={withDialogFooterSpacing()}
      footer={
        <div className="flex gap-3 justify-content-end">
          <Button
            label="Закрыть"
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={onHide}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-3 p-2">
        <div className="flex align-items-center gap-2">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '1.5rem', color: '#ef4444' }}
          />
          <span className="font-medium">Не удалось подобрать исполнителей</span>
        </div>

        <div
          className="flex flex-column p-3 border-1 border-round"
          style={{
            backgroundColor: '#fef2f2',
            borderColor: '#fecaca',
            borderRadius: '8px',
            gap: '8px'
          }}
        >
          {errors.length > 0 ? (
            errors.map((error, index) => (
              <div
                key={index}
                className="flex align-items-center gap-2"
              >
                <i
                  className="pi pi-times-circle"
                  style={{ fontSize: '0.875rem', color: '#dc2626' }}
                />
                <span style={{ color: '#991b1b' }}>
                  {errorMessages[error] || error}
                </span>
              </div>
            ))
          ) : (
            <div className="flex align-items-center gap-2">
              <i
                className="pi pi-times-circle"
                style={{ fontSize: '0.875rem', color: '#dc2626' }}
              />
              <span style={{ color: '#991b1b' }}>
                Неизвестная ошибка при подборе исполнителей
              </span>
            </div>
          )}
        </div>

        <div className="text-sm text-gray-500 mt-1">
          Пожалуйста, скорректируйте параметры наряда (плановые сроки, тип
          наряда, участок) и повторите подбор.
        </div>
      </div>
    </Dialog>
  );
};
